import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { MoveRight } from "lucide-react";
import { skipToken } from "@reduxjs/toolkit/query";

import {
  useGetCertificationProgressQuery,
  useSwitchTrackMutation,
} from "@/redux/service";
import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import {
  buildModuleOverviews,
  countTrackRequirements,
} from "@/domains/certifications/progress";
import { getContentKey, getPathCopy } from "@/domains/certifications/utils";
import {
  SwitchPathBullet,
  SwitchPathModalProps,
} from "@/domains/certifications/components/types";
import {
  ENROLLMENT_STATES,
  MODULE_STATUSES,
} from "@/domains/certifications/constants";

import "./styles.css";

const MODAL_WIDTH = 660;

const formatWeeks = (t: TFunction, positions: number[]) => {
  const first = positions[0];
  const last = positions[positions.length - 1];
  if (first === undefined || last === undefined) return "";
  if (positions.length === 1) {
    return t("certification.switchPath.weeksOne", { week: first });
  }
  if (positions.length === 2) {
    return t("certification.switchPath.weeksTwo", { first, last });
  }
  if (last - first + 1 === positions.length) {
    return t("certification.switchPath.weeksRange", { first, last });
  }
  return t("certification.switchPath.weeksList", {
    list: positions.slice(0, -1).join(", "),
    last,
  });
};

const SwitchPathModal = ({
  open,
  certification,
  targetTrack,
  onClose,
  onSwitched,
}: SwitchPathModalProps) => {
  const { t } = useTranslation();
  const enrollment =
    certification.enrollment?.state === ENROLLMENT_STATES.ACTIVE
      ? certification.enrollment
      : null;
  const { data: progress } = useGetCertificationProgressQuery(
    open && enrollment ? certification.slug : skipToken,
  );
  const [switchTrack, { isLoading, isError, reset }] = useSwitchTrackMutation();

  const contentKey = getContentKey(certification.type);
  const currentTrack = certification.tracks.find(
    (track) => track.id === enrollment?.selectedTrack,
  );

  const bullets = useMemo((): SwitchPathBullet[] => {
    if (!progress || !currentTrack || !targetTrack) return [];

    const currentWeeks = buildModuleOverviews(
      certification,
      progress,
      currentTrack.id,
    );
    const targetWeeks = buildModuleOverviews(
      certification,
      progress,
      targetTrack.id,
    );
    const ahead = currentWeeks
      .filter((week) => week.status !== MODULE_STATUSES.COMPLETE)
      .map((week) => week.position);
    const needExtraWork = currentWeeks
      .filter(
        (week, index) =>
          week.status === MODULE_STATUSES.COMPLETE &&
          targetWeeks[index]?.status !== MODULE_STATUSES.COMPLETE,
      )
      .map((week) => week.position);
    const asksMore =
      countTrackRequirements(certification, targetTrack.id) >
      countTrackRequirements(certification, currentTrack.id);
    const currentCopy = getPathCopy(t, contentKey, currentTrack);
    const targetCopy = getPathCopy(t, contentKey, targetTrack);

    const result: SwitchPathBullet[] = [];
    if (ahead.length > 0) {
      result.push({
        key: "ahead",
        tone: "primary",
        lead: t(
          asksMore
            ? "certification.switchPath.aheadMore"
            : "certification.switchPath.aheadLess",
          { weeks: formatWeeks(t, ahead) },
        ),
        body: targetCopy.switchAhead,
      });
    }
    if (needExtraWork.length > 0) {
      result.push({
        key: "extraWork",
        tone: "warning",
        lead: t("certification.switchPath.extraWork", {
          weeks: formatWeeks(t, needExtraWork),
        }),
        body: t(`${contentKey}.paths.${targetTrack.slug}.switchExtraWork`, {
          currentPath: currentCopy.title,
          credential: targetCopy.credential,
          defaultValue: "",
        }),
      });
    }
    result.push({
      key: "kept",
      tone: "primary",
      lead: t("certification.switchPath.keptLead"),
      body: t("certification.switchPath.keptBody"),
    });
    return result;
  }, [progress, certification, currentTrack, targetTrack, contentKey, t]);

  if (!enrollment || !currentTrack || !targetTrack) return null;

  const currentCopy = getPathCopy(t, contentKey, currentTrack);
  const targetCopy = getPathCopy(t, contentKey, targetTrack);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    const result = await switchTrack({
      enrollmentId: enrollment.id,
      certificationSlug: certification.slug,
      trackId: targetTrack.id,
    });
    if (!("error" in result)) {
      reset();
      onSwitched();
    }
  };

  return (
    <BaseModal
      open={open}
      width={MODAL_WIDTH}
      onCancel={handleClose}
      closable={!isLoading}
      maskClosable={!isLoading}
      keyboard={!isLoading}
      title={t("certification.switchPath.title", { path: targetCopy.title })}
      footer={
        <div className="flex flex-wrap justify-end items-center gap-200">
          <BaseButton
            variant={BUTTON_VARIANTS.TERTIARY}
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("buttons.cancel")}
          </BaseButton>

          <BaseButton
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!progress}
          >
            {t("certification.switchPath.confirm", {
              path: targetCopy.shortTitle,
            })}
          </BaseButton>
        </div>
      }
    >
      <div className="flex flex-col gap-400">
        <div className="switch-path-transition flex items-center gap-300">
          <span className="switch-path-from body-s-regular">
            {currentCopy.shortTitle}
          </span>

          <span className="switch-path-arrow flex-1 flex items-center">
            <span className="switch-path-line flex-1" />

            <MoveRight size={16} aria-hidden />
          </span>

          <span className="switch-path-to body-s-medium">
            {targetCopy.shortTitle}
          </span>
        </div>

        <p className="switch-path-intro body-m-regular">
          {t("certification.switchPath.intro")}
        </p>

        {progress ? (
          <ul className="switch-path-bullets flex flex-col gap-300">
            {bullets.map((bullet) => (
              <li
                key={bullet.key}
                className={`switch-path-bullet switch-path-bullet-${bullet.tone} body-m-regular`}
              >
                <strong className="switch-path-bullet-lead body-m-medium">
                  {bullet.lead}
                </strong>{" "}
                {bullet.body}
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex justify-center">
            <BaseSpinner />
          </div>
        )}

        {isError && (
          <p className="switch-path-error body-s-regular" role="alert">
            {t("certification.switchPath.error")}
          </p>
        )}
      </div>
    </BaseModal>
  );
};

export default SwitchPathModal;
